import { getErrorMessage } from "@brains/utils/error";
import type { BaseEntity, EntityRegistry } from "./types";
import {
  applyVisibilityToMarkdown,
  extractVisibilityFromMarkdown,
} from "./frontmatter";
import type { Logger } from "@brains/utils/logger";
import type { EntityData } from "./entity-data";
import { toEntityValidationError } from "./errors";

/**
 * EntitySerializer handles conversion between entities and markdown
 * Extracted from EntityService for single responsibility
 */
export class EntitySerializer {
  private entityRegistry: EntityRegistry;
  private logger: Logger;

  constructor(entityRegistry: EntityRegistry, logger: Logger) {
    this.entityRegistry = entityRegistry;
    this.logger = logger.child("EntitySerializer");
  }

  /**
   * Serialize an entity to markdown format
   */
  public serializeEntity(entity: BaseEntity): string {
    const adapter = this.entityRegistry.getAdapter(entity.entityType);
    return applyVisibilityToMarkdown(
      adapter.toMarkdown(entity),
      entity.visibility,
    );
  }

  /**
   * Deserialize markdown content to an entity (partial)
   * Returns parsed fields from markdown - caller should merge with metadata
   */
  public deserializeEntity(
    markdown: string,
    entityType: string,
  ): Partial<BaseEntity> {
    const adapter = this.entityRegistry.getAdapter(entityType);
    let parsedFromMarkdown: Partial<BaseEntity>;
    try {
      parsedFromMarkdown = adapter.fromMarkdown(markdown);
    } catch (error) {
      throw toEntityValidationError(entityType, error) ?? error;
    }
    const {
      metadata: parsedMetadata,
      visibility: _parsedVisibility,
      ...parsed
    } = parsedFromMarkdown;
    // Left unset when the file declares no visibility, so callers can tell
    // "the file said nothing" from "the file said public".
    const visibility = extractVisibilityFromMarkdown(markdown);
    const metadata = this.stripPolicyMetadata(parsedMetadata ?? {});
    return {
      ...parsed,
      ...(visibility && { visibility }),
      metadata,
    };
  }

  /**
   * Convert a database row to a validated entity, or null if parsing/validation fails
   */
  public async convertToEntity(
    entityData: EntityData,
    reportErrors: boolean = true,
  ): Promise<BaseEntity | null> {
    try {
      return this.reconstructEntity(entityData);
    } catch (error) {
      // Bounded reads omit diagnostics that can contain raw payloads/identifiers.
      if (reportErrors) {
        const errorMessage = getErrorMessage(error);
        this.logger.error(
          `Failed to parse entity of type ${entityData.entityType} with ID ${entityData.id}: ${errorMessage}`,
        );
      }
      return null;
    }
  }

  /**
   * Convert multiple database rows to entities, skipping any that fail validation
   */
  public async convertToEntities(
    rows: EntityData[],
    entityType: string,
    reportErrors: boolean = true,
  ): Promise<BaseEntity[]> {
    const entityList: BaseEntity[] = [];

    for (const entityData of rows) {
      try {
        entityList.push(this.reconstructEntity(entityData));
      } catch (error) {
        // Bounded reads omit diagnostics that can contain raw payloads/identifiers.
        if (reportErrors) {
          const errorMessage = getErrorMessage(error);
          this.logger.error(
            `Failed to parse entity of type ${entityType} with ID ${entityData.id}: ${errorMessage}`,
          );
        }
      }
    }

    return entityList;
  }

  /**
   * Reconstruct a typed entity from a database row by merging DB fields,
   * metadata, and adapter-parsed content, then validating against the schema
   */
  public reconstructEntity(entityData: EntityData): BaseEntity {
    const adapter = this.entityRegistry.getAdapter(entityData.entityType);
    const parsedContent = adapter.fromMarkdown(entityData.content);
    // Strip parsed metadata — DB metadata is the source of truth.
    // `system_update({ fields })` mutates DB metadata without touching
    // markdown, so parsed frontmatter can be stale. Body-parsed top-level
    // fields (e.g. `about`, `skills`) still land via parsedRest.
    const {
      metadata: _parsedMeta,
      visibility: _parsedVisibility,
      ...parsedRest
    } = parsedContent;

    const entity: unknown = {
      id: entityData.id,
      entityType: entityData.entityType,
      content: entityData.content,
      contentHash: entityData.contentHash,
      created: new Date(entityData.created).toISOString(),
      updated: new Date(entityData.updated).toISOString(),
      visibility: entityData.visibility,
      ...parsedRest,
      ...this.stripPolicyMetadata(entityData.metadata),
      metadata: this.stripPolicyMetadata(entityData.metadata),
    };

    return adapter.schema.parse(entity);
  }

  /**
   * Prepare entity for database storage
   */
  public prepareEntityForStorage(
    entity: BaseEntity,
    entityType: string,
  ): {
    markdown: string;
    metadata: Record<string, unknown>;
  } {
    // Get adapter for the entity type
    const adapter = this.entityRegistry.getAdapter(entityType);

    // Convert to markdown using adapter
    const markdown = applyVisibilityToMarkdown(
      adapter.toMarkdown(entity),
      entity.visibility,
    );

    // Extract metadata using adapter, keeping visibility as a top-level field.
    const metadata = this.stripPolicyMetadata(adapter.extractMetadata(entity));

    return { markdown, metadata };
  }

  private stripPolicyMetadata(
    metadata: Record<string, unknown>,
  ): Record<string, unknown> {
    const { visibility: _visibility, ...rest } = metadata;
    return rest;
  }
}
